// Country-package currency map - driven by backend IHRLocalizationProvider.DefaultCurrency values.
// Add rows here when a new country package is added to the backend.
const CURRENCY_META: Record<string, { symbol: string; locale: string }> = {
  PKR: { symbol: 'Rs',  locale: 'en-PK' },
  AED: { symbol: 'AED', locale: 'en-AE' },
  USD: { symbol: '$',   locale: 'en-US' },
  GBP: { symbol: '£',   locale: 'en-GB' },
  EUR: { symbol: '€',   locale: 'de-DE' },
  SAR: { symbol: 'SAR', locale: 'ar-SA' },
  OMR: { symbol: 'OMR', locale: 'ar-OM' },
  QAR: { symbol: 'QAR', locale: 'ar-QA' },
  KWD: { symbol: 'KWD', locale: 'ar-KW' },
  BHD: { symbol: 'BHD', locale: 'ar-BH' },
  INR: { symbol: '₹',   locale: 'en-IN' },
  BDT: { symbol: '৳',   locale: 'bn-BD' },
};

// UAE's new official Dirham sign, unveiled by the Central Bank 2025-03-27.
// Accepted into Unicode as U+20C3, but that codepoint only ships with Unicode
// 18.0 (Sept 2026) and font support will lag further behind, so most
// browsers/OSes render it as a missing-glyph box ("tofu") today. The canvas-measurement
// detector below is DISABLED — see the comment on resolveAedSymbol() further down for why.
const DIRHAM_SIGN = '⃃';
let dirhamGlyphSupported: boolean | null = null;

const detectDirhamGlyphSupport = (): boolean => {
  if (dirhamGlyphSupported !== null) return dirhamGlyphSupported;
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return (dirhamGlyphSupported = false);
    ctx.font = '32px sans-serif';
    const targetWidth = ctx.measureText(DIRHAM_SIGN).width;
    // A Private Use Area codepoint no real font defines — the default
    // sans-serif stack renders it as the browser's generic missing-glyph box.
    // If the dirham sign measures identically, it's almost certainly falling
    // back to that same box rather than a real glyph.
    const missingGlyphWidth = ctx.measureText('').width;
    return (dirhamGlyphSupported = targetWidth > 0 && targetWidth !== missingGlyphWidth);
  } catch {
    return (dirhamGlyphSupported = false);
  }
};

// DISABLED — docs/60-UAE-CONFORMANCE-CHECK.md section 1.14 and the standing memory note
// (uae-dirham-new-symbol-pending) both flag this: detectDirhamGlyphSupport() fires on ANY
// font that happens to render something at this codepoint, not only a verified
// Unicode-18.0-compliant one — a custom or embedded font defining an unrelated glyph at
// U+20C3 would flip every money screen to the new symbol with no code change and no
// warning, live in front of a client. Currency is the text "AED", never U+20C3, full stop,
// until there is an explicit go/no-go decision to re-enable detection. The detector above
// is left in place, unused, so re-enabling later is a one-line change once Unicode 18.0 /
// font support is actually confirmed — not deleted, since it is otherwise sound.
const resolveAedSymbol = (): string => 'AED';

/**
 * Called once after login, before first render, when AppContext resolves the org's localization.
 * Writes the derived symbol + locale to localStorage so formatCurrency() picks them up app-wide
 * without a page reload.
 */
export const seedCurrencyFromCode = (currencyCode: string): void => {
  const code = currencyCode?.toUpperCase();
  const entry = CURRENCY_META[code] ?? CURRENCY_META['PKR'];
  const symbol = code === 'AED' ? resolveAedSymbol() : entry.symbol;
  localStorage.setItem('globalCurrencySymbol', symbol);
  localStorage.setItem('globalCurrencyLocale', entry.locale);
};

export const getGlobalCurrencySymbol = (): string =>
  localStorage.getItem('globalCurrencySymbol') || 'Rs';

/** Alias for getGlobalCurrencySymbol - use this in new code */
export const getCurrencySymbol = getGlobalCurrencySymbol;

/**
 * Force the Latin numbering system onto a locale tag.
 *
 * Five rows in CURRENCY_META resolve to Arabic tags — SAR→`ar-SA`, OMR→`ar-OM`, QAR→`ar-QA`,
 * KWD→`ar-KW`, BHD→`ar-BH` — and those tags default to the `arab` numbering system. So an org on
 * any Gulf currency other than AED has been rendering **money in Arabic-Indic digits (`١٬٢٣٤`)
 * inside an otherwise English UI**, which is a defect that predates any Arabic work here.
 *
 * Pinning `nu-latn` fixes that and enforces the product-wide decision that digits are always
 * Western — Gulf business and financial usage is Western-digit, and it keeps figures consistent
 * with WPS/SIF files and bank exports. See docs/43-I18N-ARABIC-DESIGN.md §2.
 *
 * `Intl` accepts at most one `-u-` section, so an existing extension is merged into rather than
 * appended to — appending a second `-u-` yields an invalid tag and throws.
 */
const pinLatinDigits = (tag: string): string => {
  if (/-u-.*\bnu-/.test(tag)) return tag;
  if (tag.includes('-u-')) return tag.replace('-u-', '-u-nu-latn-');
  return `${tag}-u-nu-latn`;
};

export const getGlobalCurrencyLocale = (): string =>
  pinLatinDigits(localStorage.getItem('globalCurrencyLocale') || 'en-PK');

/** Manual user-driven currency switch (Settings page etc). Reloads to apply everywhere. */
export const setGlobalCurrency = (symbol: string, locale: string): void => {
  localStorage.setItem('globalCurrencySymbol', symbol);
  localStorage.setItem('globalCurrencyLocale', locale);
  window.location.reload();
};

/**
 * Symbol + locale for an explicit ISO currency code.
 *
 * Returns null for a missing or unrecognised code so callers fall back to the
 * global (org-level) currency rather than silently mislabelling an amount.
 */
const metaForCode = (code?: string | null): { symbol: string; locale: string } | null => {
  if (!code) return null;
  return CURRENCY_META[code.toUpperCase()] ?? null;
};

/**
 * Format a money amount.
 *
 * `currencyCode` is the currency the AMOUNT ITSELF is denominated in - a payroll
 * row's own `currency`, a run's `currency`, a salary mapping's `currency`. Always
 * pass it where the data carries one: an employee assigned AED must not be shown
 * in Rs just because the org's country default is PK. Omitting it falls back to
 * the org-wide currency seeded at login, which is correct only for figures that
 * genuinely have no per-row currency.
 *
 * An unrecognised code is rendered as the bare code (`"XYZ 1,000"`) rather than
 * being dropped, so a currency the frontend doesn't know about is still labelled
 * honestly instead of wearing the wrong symbol.
 */
export const formatCurrency = (
  amount: number | string | undefined | null,
  currencyCode?: string | null,
  /** Fixed decimals, for a money COLUMN where 189,650 next to 947,743.73 would not line up. */
  fractionDigits?: number,
): string => {
  const meta = metaForCode(currencyCode);
  const symbol = meta?.symbol ?? (currencyCode ? currencyCode.toUpperCase() : getGlobalCurrencySymbol());
  const locale = meta?.locale ?? getGlobalCurrencyLocale();
  const val = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (val === undefined || val === null || isNaN(val)) {
    return `${symbol} 0`;
  }

  const min = fractionDigits ?? 0;
  const max = fractionDigits ?? 2;
  return `${symbol} ${val.toLocaleString(locale, { minimumFractionDigits: min, maximumFractionDigits: max })}`;
};

/**
 * Format a date using the org's locale (seeded from country package on login).
 * Accepts Date | string | null | undefined. Returns '-' for invalid/missing values.
 * Defaults to { dateStyle: 'medium' } - e.g. "Jul 2, 2026" in en-US, "02 Jul 2026" in en-PK.
 */
export const formatDate = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string => {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(getGlobalCurrencyLocale(), options);
};

/**
 * Format a date+time using the org's locale.
 * Defaults to { dateStyle: 'medium', timeStyle: 'short' }.
 */
export const formatDateTime = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string => {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString(getGlobalCurrencyLocale(), options);
};

/**
 * Compact currency formatter for dashboard KPI cards.
 * e.g. Rs 1.2M, AED 500K, $ 2.3B
 */
export const formatCompactCurrency = (
  amount: number | undefined | null,
  currencyCode?: string | null,
): string => {
  const meta = metaForCode(currencyCode);
  const symbol = meta?.symbol ?? (currencyCode ? currencyCode.toUpperCase() : getGlobalCurrencySymbol());
  const val = amount ?? 0;

  if (Math.abs(val) >= 1_000_000_000) return `${symbol} ${(val / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(val) >= 1_000_000)     return `${symbol} ${(val / 1_000_000).toFixed(1)}M`;
  if (Math.abs(val) >= 1_000)         return `${symbol} ${(val / 1_000).toFixed(1)}K`;
  return formatCurrency(val, currencyCode);
};
